<template>
  <div class="sf-layer-rounded sf-layer-white sf-pad-2 sf-scrollbar">
    <SfBannerInline v-if="failed" :variant="bannerVariants.warning">
      <template #title>
        <span class="tw-font-semibold">
          {{ I18n.t('reconciliation_review.item_error') }}
        </span>
      </template>
    </SfBannerInline>
    <template v-else>
      <WorkflowReviewLoader
        v-if="loading"
        data-testid="review-item-solid-loader"
      />
      <div ref="mountNode" data-testid="review-item-solid" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { SfLog } from '@app/adapters/logger'
import { addStylesheets } from '@app/adapters/solid/add-stylesheets'
import { loadTemplateEntry } from '@app/adapters/solid/load-template-entry'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'
import { bannerVariants } from '@app/shared/types/banner'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type { SolidTemplateBoot } from '@app/shared/types/sf-data'
import WorkflowReviewLoader from './workflow-review-loader.vue'

const I18n = useI18n()

const props = defineProps<{ solidTemplate: SolidTemplateBoot }>()
const mountNode = ref<HTMLElement | null>(null)
const loading = ref(true)
const failed = ref(false)

let dispose: (() => void) | null = null
let unmounted = false

onMounted(async () => {
  try {
    addStylesheets(props.solidTemplate.stylesheetUrls)
    const entry = await loadTemplateEntry(props.solidTemplate.entryUrl)

    // Navigated away while the module (entry) was loading.
    if (unmounted || !mountNode.value) return

    dispose = entry.mount(mountNode.value, props.solidTemplate.requestContext)
    loading.value = false
  } catch (error) {
    failed.value = true
    SfLog.error('Solid template failed to boot on workflow review', {
      error: error as Error,
      extra: { entryUrl: props.solidTemplate.entryUrl },
    })
  }
})

onUnmounted(() => {
  unmounted = true
  dispose?.()
  dispose = null
})
</script>
